import { Address } from './Address';
import { PaymentType } from './Payment';
import { InstallerOffer } from './Quote';
import { BatteryChoice, PanelChoice, SpecChoice } from './Visitor';

export type QuoteSet = {
  _id: string;
  address?: Address;
  bill: number;
  quote_ids: string[];
  quotes: InstallerOffer[];
  status: string;
  ts: string;
  vid: string;
};

// 202 body while the quote set builds; ts is missing until the quote set doc exists
export type GeneratingQuoteSet = { status: 'generating'; ts?: string };

export type QuoteSetResponse = GeneratingQuoteSet | QuoteSet;

export const monthlyPaymentsRangeFromQuoteSet = (
  quoteSet: QuoteSet,
  { batteryCount = 0, panelCount }: SpecChoice = {}
) => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  const considerMinMax = (n: number) => {
    if (Number.isFinite(n)) {
      if (n < min) min = n;
      if (n > max) max = n;
    }
  };

  quoteSet.quotes.forEach((offer) => {
    if (!offer.quotes?.length) {
      return;
    }

    const closest = pickSolarQuote(offer, { count: panelCount });
    const batteryOffer = batteryCount > 0 ? offer.battery_options?.find((o) => o.count === batteryCount) : undefined;
    const batteryLoan = batteryOffer?.financings.find((f) => f.type === PaymentType.Loan)?.monthly_payment ?? 0;
    const batteryPpa = batteryOffer?.financings.find((f) => f.type === PaymentType.PPA)?.monthly_payment ?? 0;

    const loanPricing = closest.loan_pricing;
    if (loanPricing) {
      considerMinMax(loanPricing.payment + batteryLoan);
    }

    const ppaPricing = closest.ppa;
    if (ppaPricing) {
      // includes_storage already bundles the battery; don't double-add.
      const ppaBattery = ppaPricing.includes_storage ? 0 : batteryPpa;
      considerMinMax(ppaPricing.initial_monthly_payment + ppaBattery);
    }
  });

  return { max, min };
};

// count wins over perc when both are set. perc defaults to 1 (100% offset).
// Returns undefined when neither is set, or when offers is empty, so callers can preserve the visitor's existing panel choice.
export const resolvePanelChoice = (
  offers: InstallerOffer[],
  { count, perc }: Partial<PanelChoice>
): PanelChoice | undefined => {
  if (offers.length === 0 || (count == null && perc == null)) {
    return undefined;
  }

  const quote = pickSolarQuote(offers[0], { count, perc });
  return { count: quote.panel_count, perc: quote.solar_percentage };
};

// count wins over days when both are set. Returns undefined when neither is set, or when offers is empty, so callers can preserve the visitor's existing battery choice.
export const resolveBatteryChoice = (
  offers: InstallerOffer[],
  { count, days }: Partial<BatteryChoice>
): BatteryChoice | undefined => {
  if (offers.length === 0) {
    return undefined;
  }

  if (count != null) {
    if (count <= 0) return { count: 0, days: 0 };
    const match = offers.flatMap((offer) => offer.battery_options ?? []).find((o) => o.count === count);
    return match ? { count: match.count, days: match.days } : { count, days: 0 };
  }

  if (days != null) {
    const batteryOffers = offers.flatMap((offer) => offer.battery_options ?? []);
    const match = batteryOffers.find((o) => o.days >= days) ?? batteryOffers.at(-1);
    return match ? { count: match.count, days: match.days } : { count: 0, days: 0 };
  }

  return undefined;
};

export const pickSolarQuote = (offer: InstallerOffer, { count, perc = 1 }: Partial<PanelChoice>) => {
  if (count != null) {
    return offer.quotes.reduce((best, q) =>
      Math.abs(q.panel_count - count) < Math.abs(best.panel_count - count) ? q : best
    );
  }

  return offer.quotes.reduce((best, q) =>
    Math.abs(q.solar_percentage - perc) < Math.abs(best.solar_percentage - perc) ? q : best
  );
};
